import { useMemo } from 'react'
import { useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

import { AppLayout } from './AppLayout'
import { TopBarBackButton } from './topbar'
import { Card } from './ui/card'
import { ListSkeleton } from './ui/skeleton-presets'
import { EmptyState } from './EmptyState'
import { ArrowRight, CalendarCheck, ChevronRight } from './icons'

import { useMyTasks } from '../lib/hooks/task'

dayjs.extend(relativeTime)

type TaskRow = {
  id: string
  name: string
  status: string
  createdAt: string | Date
  tripLeg?: {
    departureAirportIcao?: string | null
    arrivalAirportIcao?: string | null
    trip?: { tripName?: string | null } | null
  } | null
}

export function MyTasks() {
  const navigate = useNavigate()
  const { data: tasks = [], isPending } = useMyTasks()

  const visibleTasks = useMemo(
    () =>
      (tasks as TaskRow[]).filter(
        (t) => t.status === 'Draft' || t.status === 'Submitted'
      ),
    [tasks]
  )

  return (
    <AppLayout
      topBar={<TopBarBackButton title="Tasks" />}
    >
      {isPending ? (
        <ListSkeleton />
      ) : visibleTasks.length === 0 ? (
        <EmptyState
          icon={CalendarCheck}
          title="No tasks"
          description="Tasks assigned on your flights will appear here."
        />
      ) : (
        <Card className="flex flex-col gap-0 p-0 overflow-hidden">
          {visibleTasks.map((task, idx) => (
            <div key={task.id}>
              <TaskListRow
                task={task}
                onClick={() =>
                  navigate({
                    to: '/task/$taskId',
                    params: { taskId: task.id },
                  })
                }
              />
              {idx < visibleTasks.length - 1 && (
                <div className="h-px bg-border-L1 mx-4" />
              )}
            </div>
          ))}
        </Card>
      )}
    </AppLayout>
  )
}

function TaskListRow({
  task,
  onClick,
}: {
  task: TaskRow
  onClick: () => void
}) {
  const tripName = task.tripLeg?.trip?.tripName ?? null
  const depIcao = task.tripLeg?.departureAirportIcao ?? null
  const arrIcao = task.tripLeg?.arrivalAirportIcao ?? null
  const isDraft = task.status === 'Draft'

  return (
    <button
      onClick={onClick}
      className="w-full text-left flex items-start gap-3 px-4 py-3"
    >
      <div className="flex flex-1 flex-col gap-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold text-content-primary-bold leading-5 truncate">
            {task.name}
          </p>
          {isDraft && (
            <span className="size-2 shrink-0 rounded-full bg-content-info-bold" />
          )}
        </div>
        {(tripName || depIcao) && (
          <div className="flex items-center gap-3 text-sm text-content-primary-subtle leading-5">
            {tripName && <span className="truncate">{tripName}</span>}
            {depIcao && arrIcao && (
              <span className="flex items-center gap-2 shrink-0">
                <span>{depIcao}</span>
                <ArrowRight className="size-3 text-content-primary-subtle-mid" />
                <span>{arrIcao}</span>
              </span>
            )}
          </div>
        )}
        <p className="text-xs font-semibold text-content-primary-subtle leading-4">
          {dayjs(task.createdAt).fromNow()}
        </p>
      </div>
      <ChevronRight className="size-5 shrink-0 text-content-primary-subtle-mid mt-1" />
    </button>
  )
}
